"use client";

import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { FormField, FormItem, FormControl, FormMessage, FormLabel } from "@/components/ui/form";
import { useFormContext } from "react-hook-form";
import { StepProps, BaseFormData } from "../components/types";
import { ValidationRules } from "../components/FormValidation";
import { cn } from "@/lib/utils";
import parse from "html-react-parser";

// Configuration interface for SingleSelectionStep
export interface SingleSelectionStepConfig {
  fieldName: string; // The form data field name (e.g., 'financingType', 'productType', etc.)
  optionsKey: string; // The key in formOptions to get the options from
  title: string; // The question title
  description?: string; // Optional description below the title
}

type SingleSelectionStepProps<T = BaseFormData> = StepProps<T>;

export const SingleSelectionStep = <T extends BaseFormData = BaseFormData>(props: SingleSelectionStepProps<T>) => {
  const { 
    formData, 
    title, 
    description, 
    formOptions,
  } = props;
  
  const { control } = useFormContext();

  // Extract stepConfig and infoBoxText from props with type safety
  const stepConfig = props.stepConfig as SingleSelectionStepConfig | undefined;
  const infoBoxText = props.infoBoxText as string | undefined;

  // Default configuration for backward compatibility
  const config = stepConfig || {
    fieldName: 'financingType',
    optionsKey: 'financingTypes',
    title: "What's your preferred type of invoice financing?",
    description: undefined
  };

  // Get the options dynamically based on the configuration
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const options = (formOptions as any)[config.optionsKey] || [];

  return (
    <div className="flex flex-col gap-12">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <h3>
          {title}
        </h3>
        {description && (
          <p className="paragraph-lg text-muted-foreground">
            {parse(description)}
          </p>
        )}
      </div>

      {/* Preference Selection */}
      <div className="flex flex-col gap-6">
        <FormLabel className="paragraph-base text-foreground font-bold">
          {config.title}
        </FormLabel>
        {config.description && (
          <p className="paragraph-base text-muted-foreground">
            {config.description}
          </p>
        )}
        
        <FormField
          control={control}
          name={config.fieldName as never}
          rules={ValidationRules.selection("option")}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <RadioGroup
                  value={(field.value as string) ?? (formData[config.fieldName] as string)}
                  onValueChange={(value) => { field.onChange(value); }}
                  className="border border-border rounded-3xl gap-0"
                >
                  {options.map((option: { value: string; title: string; description: string }, index: number) => (
                    <Label 
                      key={option.value}
                      htmlFor={option.value}
                      className={cn(
                        "w-full px-6 py-4 flex items-center gap-6 cursor-pointer hover:bg-muted transition-colors",
                        index === 0 && "rounded-t-3xl",
                        index === options.length - 1 && "rounded-b-3xl",
                        index < options.length - 1 && "border-b border-border"
                      )}
                    >
                      <RadioGroupItem value={option.value} id={option.value} className="cursor-pointer" />
                      <div className="flex-1">
                        <div className="flex flex-col gap-1 items-start justify-center">
                          <span className="paragraph-base text-foreground font-bold">{option.title}</span>
                          {option.description && (
                            <span className="paragraph-base text-muted-foreground font-normal">
                              {option.description}
                            </span>
                          )}
                        </div>
                      </div>
                    </Label>
                  ))}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Information Box */}
      {infoBoxText && (
        <div className="bg-muted/100 p-4 rounded-lg">
          <p className="paragraph-sm text-muted-foreground [&_strong]:text-foreground">
            {parse(infoBoxText)}
          </p>
        </div>
      )}
    </div>
  );
}; 